import { AlertCircle, AppWindow, CheckCircle, Layers } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import forgeService from '../../services/forgeService';
import { useAuthStore } from '../../stores/authStore';
import { Forge, ForgeApplication } from '../../types';

interface ForgeStats {
    totalForges: number;
    deployedForges: number;
    totalApps: number;
    deployedApps: number;
    errorApps: number;
}

/**
 * Forge Apps metrics card for the main dashboard.
 * Shows forge and application stats for the user's organization.
 */
export const ForgeAppsMetricsCard: React.FC = () => {
    const { t } = useTranslation();
    const user = useAuthStore((state) => state.user);
    const [stats, setStats] = useState<ForgeStats | null>(null);
    const [loading, setLoading] = useState(true);
    // Holds an i18n key, translated at render time.
    const [error, setError] = useState<string | null>(null);
    const [hasForgeClient, setHasForgeClient] = useState<boolean | null>(null);

    useEffect(() => {
        const loadForgeStats = async () => {
            // Need organization_id to check forge client mapping
            const organizationId = user?.organization_id;

            if (!organizationId) {
                setHasForgeClient(false);
                setStats({
                    totalForges: 0,
                    deployedForges: 0,
                    totalApps: 0,
                    deployedApps: 0,
                    errorApps: 0,
                });
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Fetch the organization's forge client mapping
                const mapping = await forgeService.getOrganizationClientMapping(organizationId);
                const forgeClientId = mapping.forge_client_id;

                if (!forgeClientId) {
                    setHasForgeClient(false);
                    setStats({
                        totalForges: 0,
                        deployedForges: 0,
                        totalApps: 0,
                        deployedApps: 0,
                        errorApps: 0,
                    });
                    setLoading(false);
                    return;
                }

                setHasForgeClient(true);

                // Fetch forges for this client
                const forges: Forge[] = await forgeService.listForges(forgeClientId);

                // Fetch all applications for these forges
                let allApps: ForgeApplication[] = [];
                for (const forge of forges) {
                    try {
                        const apps = await forgeService.listApplications(forge.forge_id);
                        allApps = [...allApps, ...apps];
                    } catch {
                        // Continue if one forge fails
                    }
                }

                // Calculate stats
                const deployedForges = forges.filter(f => f.status === 'deployed').length;
                const deployedApps = allApps.filter(a => a.status === 'deployed').length;
                const errorApps = allApps.filter(a =>
                    a.status === 'error' || a.status === 'build_failed'
                ).length;

                setStats({
                    totalForges: forges.length,
                    deployedForges,
                    totalApps: allApps.length,
                    deployedApps,
                    errorApps,
                });
            } catch (err) {
                console.error('Failed to load forge stats:', err);
                setError('dashboard.forgeApps.loadError');
                setHasForgeClient(null); // Unknown state on error
                setStats({
                    totalForges: 0,
                    deployedForges: 0,
                    totalApps: 0,
                    deployedApps: 0,
                    errorApps: 0,
                });
            } finally {
                setLoading(false);
            }
        };

        loadForgeStats();
    }, [user?.organization_id]);

    // Loading skeleton
    if (loading) {
        return (
            <div className="bg-card border border-border rounded-xl p-6">
                <div className="h-5 w-32 bg-muted animate-pulse rounded mb-4" />
                <div className="space-y-3">
                    <div className="h-16 bg-muted animate-pulse rounded-lg" />
                    <div className="h-16 bg-muted animate-pulse rounded-lg" />
                </div>
            </div>
        );
    }

    // No forge client linked
    if (hasForgeClient === false) {
        return (
            <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                    {t('dashboard.forgeApps.title', 'Forge')}
                </h3>
                <div className="text-center py-4">
                    <Layers className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                        {t('dashboard.forgeApps.noForge', 'No Forge linked to this organization')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-foreground">
                    {t('dashboard.forgeApps.title', 'Forge')}
                </h3>
                <Link
                    to="/forges"
                    className="text-xs text-primary hover:underline"
                >
                    {t('dashboard.forgeApps.viewAll', 'View all')}
                </Link>
            </div>

            {error && (
                <p className="text-xs text-yellow-600 dark:text-yellow-400 mb-3">
                    {t(error)}
                </p>
            )}

            <div className="space-y-3">
                {/* Forges row */}
                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                            <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">
                                {t('dashboard.forgeApps.forges', 'Forges')}
                            </p>
                            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                {stats?.totalForges || 0}
                            </p>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">{stats?.deployedForges || 0}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('dashboard.forgeApps.deployed', 'deployed')}
                        </p>
                    </div>
                </div>

                {/* Applications row */}
                <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
                            <AppWindow className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground">
                                {t('dashboard.forgeApps.applications', 'Applications')}
                            </p>
                            <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                                {stats?.totalApps || 0}
                            </p>
                        </div>
                    </div>
                    <div className="text-right space-y-1">
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">{stats?.deployedApps || 0}</span>
                        </div>
                        {(stats?.errorApps ?? 0) > 0 && (
                            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                                <AlertCircle className="h-4 w-4" />
                                <span className="text-sm font-medium">{stats?.errorApps}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
